import { gql } from '@apollo/client';
import { FULL_CHANNEL } from './pages/queries.tsx';
import { BASE_DOCUMENT, BASE_FIELD, PRIMITIVE_FIELD, SELECT_FIELD_FRAGMENT } from '../../document/queries.ts';

export const FULL_LIST_OF_SERVICES = gql`
    fragment FullListOfServices on ListOfServices {
        chapters {
            number
            numberTotal
            numberRelevant
            numberCompleted
            numberIrrelevantOpen
            numberIrrelevantCompleted
            numberRelevantOpen
            numberRelevantCompleted
            containsHighlightKeywords   
            header
            pageIndex
        }
        numberOfPositions
        numberOfRelevantPositions
        numberOfPretexts
        ${BASE_DOCUMENT}
        
        customer {
            name
            customerNumber
            customerNumber2
            customerType
            iln
            misc
            contactPerson
            ${BASE_FIELD}
            internalNote

            address {
                addressId
                name
                postcode
                city
                country
                phone
                email
                companyName
                streetAndNr
                misc
                bbox
                pageIndex
                predictionConfidence
            }
        }
        
        deliveryAddress {
            addressId
            addressId2
            name
            postcode
            city
            countrySubdivision
            country
            phone
            email
            companyName
            companyName2
            companyName3
            companyName4
            streetAndNr
            streetName
            streetNumber
            misc
            ${BASE_FIELD}
        }
        
        submissionDate {
            ${PRIMITIVE_FIELD}
        }

        constructionSite {
            ${PRIMITIVE_FIELD}
        }
        
        projectNumber {
            ${PRIMITIVE_FIELD}
        }

        projectName {
            ${PRIMITIVE_FIELD}
        }

        objectNumber {
            ${PRIMITIVE_FIELD}
        }

        tenderSource {
            ${PRIMITIVE_FIELD}
            choices
            prediction
        }

        customerContact {
            name
            contactId
            phone
            email
            ${BASE_FIELD}
        }
        
        contractor {
            ${PRIMITIVE_FIELD}
        }

        comment {
            ${PRIMITIVE_FIELD}
        }
        
        dynamicSchemaSerialized
    }
`;

const FULL_RECORD = gql`
    ${FULL_LIST_OF_SERVICES}
    ${FULL_CHANNEL}
    fragment FullRecord on ListOfServicesProcessingRecord {
        id
        createdAt
        deletedAt
        deletedBy {
            email
            firstName
            lastName
        }
        deletedReason
        assignedTo {
            id
            firstName
            lastName
            email
        }
        emailCount

        status

        sender
        subject

        submissionDate

        canFinishAssistance
        isHeaderCorrect: canFinishHeaderAssistance(ignoreThreshold: false)

        lockedBy {
            email
        }

        putOnHoldReason
        putOnHoldBy {
            email
            firstName
            lastName
        }

        assistedBy {
            email
            firstName
            lastName
        }

        assistedAt

        stepRun {
            id
            stepName
            manualOnly
            executionStatus
            error
            context
        }

        listofservicesprocessingsteprunSet {
            id
            createdAt
            updatedAt
            stepName
            manualOnly
            executionStatus
            context
            error
        }

        channel {
            ...FullChannel

            highlightKeywordList
        }

        isDiscarded
        isTestDocument
        documentType

        discardRecord {
            reason
            user {
                id
                firstName
                lastName
            }
            createdAt
            updatedAt
        }

        sourceFile {
            ... on Email {
                sender {
                    email
                    name
                }
                receivers {
                    email
                    name
                }
                subject
                messageId
                language
                receivedAt

                text
                html
                attachments {
                    cid
                    contentDisposition
                    name
                    type
                    url
                    sourcePartId
                }

                senderClientId
                prediction
                predictionConfidence
                sourceUrl

                predictionConfidences
            }

            ... on File {
                name
                type
                url
            }
        }

        sourceParts {
            id
            fileName
            mimeType
            partType
            fileUrl
            classificationLabel
            classificationConfidence
            recordId
            classificationCorrectedBy
            classificationCorrectedAt
        }

        inputFile {
            ... on File {
                name
                type
                sourceType
                url
            }
        }

        outputFile {
            ... on Export {
                files {
                    name
                    url
                }
            }
        }

        listOfServicesUnsaved {
            ...FullListOfServices
        }

        externalReference
        deliveryRecord {
            deliveryMethod
            deliveryStatus
            externalReference
            externalMessage
            externalStatus
        }
        labelingStatus

        masterdataVersionsExtracted
    }
`;

export const GET_OVERVIEW_DATA = gql`
    query {
        listOfServicesProcessingChannels {
            id
            name
        }
    }
`;

export const GET_CHANNELS_OVERVIEW_DATA = gql`
    query {
        listOfServicesProcessingChannels {
            id
            name
            emailAddress
            team {
                id
                users {
                    email
                    firstName
                    lastName
                }
            }
        }
    }
`;

export const GET_TABLE_OVERVIEW_DATA = gql`
    query ($filters: [TableFilter], $orderBy: String, $offset: Int, $first: Int) {
        filteredListOfServicesProcessingRecordsList(
            filters: $filters
            orderBy: $orderBy
            offset: $offset
            limit: $first
        ) {
            totalCount
            records {
                id
                sender
                status
                createdAt
                finishedAt
                assistedAt
                submissionDate
                assistedBy {
                    id
                    firstName
                    lastName
                    email
                }
                createdBy {
                    email
                    firstName
                    lastName
                }
                assignedTo {
                    id
                    firstName
                    lastName
                    email
                }
                deletedAt
                deletedBy {
                    id
                    firstName
                    lastName
                    email
                }
                deletedReason
                lockedBy {
                    email
                }
                stepRun {
                    id
                    stepName
                    manualOnly
                    executionStatus
                    error
                    context
                }
                channel {
                    id
                    name
                }
                isDiscarded
                deliveryRecord {
                    deliveryMethod
                    deliveryStatus
                    externalReference
                    externalMessage
                    externalStatus
                }
                discardRecord {
                    reason
                    user {
                        id
                        firstName
                        lastName
                        email
                    }
                    createdAt
                    updatedAt
                }
                isTestDocument
                canProcessAutomatically
                title
                subtitle
                attachmentsCount
                sourcePart {
                    sender {
                        email
                        name
                    }
                }
                emailCount
                putOnHoldReason
                putOnHoldBy {
                    firstName
                    lastName
                }
            }
        }
    }
`;

export const GET_ASSISTANCE_OVERVIEW_DATA = gql`
    query ($recordId: UUID!, $filters: [TableFilter]) {
        listOfServicesProcessingRecordAssistancePagination(recordId: $recordId, filters: $filters) {
            filteredCount
            position
            previousRecordId
            nextRecordId
        }
    }
`;

export const GET_ASSISTANCE_DATA = gql`
    ${FULL_RECORD}
    ${FULL_LIST_OF_SERVICES}
    query ($id: UUID!, $chapter: String) {
        listOfServicesProcessingRecord(id: $id, chapter: $chapter) {
            ...FullRecord

            dusDocument {
                pagesCount
                processedFileUrl
            }

            dusEmail {
                pagesCount
                processedFileUrl
            }
        }
    }
`;

export const GET_LIST_OF_SERVICES_UNSAVED_DATA = gql`
    ${SELECT_FIELD_FRAGMENT}
    query ($id: UUID!, $chapter: String) {
        listOfServicesProcessingRecord(id: $id, chapter: $chapter) {
            listOfServicesUnsaved {
                chapters {
                    number
                    numberTotal
                    numberRelevant
                    numberCompleted
                    numberIrrelevantOpen
                    numberIrrelevantCompleted
                    numberRelevantOpen
                    numberRelevantCompleted                   
                    containsHighlightKeywords                    
                    header
                    pageIndex
                }
                positions {
                    id
                    positionIndex: index
                    sortIndex {
                        ${PRIMITIVE_FIELD}
                    }
                    positionSource {
                        ...FullSelectField
                    }
                    chapter
                    number {
                        ${PRIMITIVE_FIELD}
                    }
                    numberRaw {
                        ${PRIMITIVE_FIELD}
                    }
                    text {
                        ${PRIMITIVE_FIELD}
                    }
                    isRelevant {
                        ${PRIMITIVE_FIELD}
                    }
                    isIncluded
                    rejectedBy {
                        ${PRIMITIVE_FIELD}
                    }
                    prediction
                    parent
                    header
                    feedbackRequest
                    articleSets {
                        id
                        isComplete
                        articles {
                            number {
                                ${PRIMITIVE_FIELD}
                            }
                            articleNumber2 {
                                ${PRIMITIVE_FIELD}
                            }
                            articleNumber3 {
                                ${PRIMITIVE_FIELD}
                            }
                            articlePartitionSpecificNumber {
                                ${PRIMITIVE_FIELD}
                            }
                            description {
                                ${PRIMITIVE_FIELD}
                            }
                            unit {
                                ...FullSelectField
                            }
                            quantity {
                                ${PRIMITIVE_FIELD}
                            }
                            comments {
                                ${PRIMITIVE_FIELD}
                            }
                            suggestions {
                                articleId1
                                articleId2
                                articleId3
                                description
                                orderUnits
                                salesUnits
                                quantity
                                unit
                                confidence
                                dynamicSchemaSerialized
                            }
                            dynamicSchemaSerialized
                        }
                    }
                    ${BASE_FIELD}
                }
            }
        }
    }
`;

// chapter -1 as we don't need any particular chapter, could be any non existing value except empty
export const GET_LIST_OF_SERVICES_UNSAVED_NUMBERS = gql`
    query ($id: UUID!) {
        listOfServicesProcessingRecord(id: $id, chapter: "-1") {
            listOfServicesUnsaved {
                chapters {
                    number
                    numberTotal
                    numberRelevant
                    numberCompleted
                    numberIrrelevantOpen
                    numberIrrelevantCompleted
                    numberRelevantOpen
                    numberRelevantCompleted
                    containsHighlightKeywords
                    header
                    pageIndex
                }
                numberOfPositions
                numberOfRelevantPositions
                numberOfPretexts
            }
        }
    }
`;

export const GET_INCOMPLETE_RELEVANT_POSITION_NUMBERS = gql`
    query ($id: UUID!) {
        listOfServicesProcessingRecord(id: $id) {
            incompleteRelevantPositionNumbers
        }
    }
`;

export const UPDATE_PROCESSING_RECORD = gql`
    mutation ($recordId: UUID!, $fieldName: String, $itemIndex: Int, $action: String!, $payload: GenericScalar) {
        listOfServicesUpdate(
            recordId: $recordId
            fieldName: $fieldName
            itemIndex: $itemIndex
            action: $action
            payload: $payload
        ) {
            record {
                canFinishAssistance
                isHeaderCorrect: canFinishHeaderAssistance(ignoreThreshold: false)
                stepRun {
                    id
                    executionStatus
                    manualOnly
                    stepName
                }
            }

            patch {
                op
                path
                from
                value
            }
        }
    }
`;

export const RESELECT_PROCESSING_RECORD = gql`
    mutation (
        $recordId: UUID!
        $fieldName: String
        $bbox: [[Float]]!
        $pageIndex: Int!
        $itemIndex: Int
        $isEmailOcr: Boolean
    ) {
        listOfServicesReselect(
            recordId: $recordId
            fieldName: $fieldName
            bbox: $bbox
            pageIndex: $pageIndex
            itemIndex: $itemIndex
            isEmailOcr: $isEmailOcr
        ) {
            record {
                canFinishAssistance
                isHeaderCorrect: canFinishHeaderAssistance(ignoreThreshold: false)
            }

            patch {
                op
                path
                from
                value
            }
        }
    }
`;

export const RETRY_STEP = gql`
    mutation ($recordId: UUID!, $stepRunId: UUID!) {
        listOfServicesRetryStep(recordId: $recordId, stepRunId: $stepRunId) {
            success
        }
    }
`;

export const REOPEN_FOR_ASSISTANCE = gql`
    mutation ($recordId: UUID!, $reason: String) {
        listOfServicesReopenForAssistance(recordId: $recordId, reason: $reason) {
            success
        }
    }
`;

export const UPLOAD_FILE = gql`
    mutation ($channelId: UUID!, $files: [Upload]!, $batchId: String, $isTestingDocument: Boolean!) {
        listOfServicesUploadFile(
            input: { channelId: $channelId, files: $files, batchId: $batchId, isTestingDocument: $isTestingDocument }
        ) {
            records {
                id
            }
            success
        }
    }
`;

export const RE_UPLOAD_FILE = gql`
    mutation ($channelId: UUID!, $files: [Upload]!, $recordId: String!, $isTestingDocument: Boolean!) {
        listOfServicesUploadFile(
            input: { channelId: $channelId, files: $files, recordId: $recordId, isTestingDocument: $isTestingDocument }
        ) {
            records {
                id
            }
            success
        }
    }
`;

export const SEND_TO_LABELING = gql`
    mutation ($recordId: UUID!) {
        listOfServicesSendToLabeling(recordId: $recordId) {
            success
        }
    }
`;

export const GET_NEXT_ASSISTANCE_RECORD = gql`
    query ($filters: [TableFilter], $channelId: UUID, $orderBy: String) {
        nextListOfServicesProcessingRecord(filters: $filters, channelId: $channelId, orderBy: $orderBy) {
            id
        }
    }
`;

export const CREATE_CHANNEL = gql`
    mutation ($name: String!, $fromExistingChannelId: UUID) {
        createListOfServicesProcessingChannel(name: $name, fromExistingChannelId: $fromExistingChannelId) {
            channel {
                id
            }
            success
        }
    }
`;

export const GET_ALL_CHANNELS = gql`
    ${FULL_CHANNEL}
    query {
        listOfServicesProcessingChannels {
            ...FullChannel
        }
    }
`;

export const LIST_OF_SERVICES_CHAPTER_ACTION = gql`
    mutation ($recordId: UUID!, $chapter: String!, $action: String!, $subchapter: String) {
        listOfServicesChapterAction(recordId: $recordId, chapter: $chapter, action: $action, subchapter: $subchapter) {
            success
        }
    }
`;

export const GET_RELATED_DOCUMENT = gql`
    query ($id: UUID!) {
        listOfServicesProcessingRecord(id: $id) {
            id
            subject
            createdAt
            finishedAt
            assistedAt
            assistedBy {
                id
                firstName
                lastName
                email
            }
            status
        }
    }
`;

export const HIGHLIGHT_ARTICLE_LONG_QUERY_DESCRIPTION = gql`
    query ($recordId: UUID!, $descriptionText: String!, $positionId: UUID, $tag: String) {
        highlightArticleLongQueryDescriptionV2(
            recordId: $recordId
            descriptionText: $descriptionText
            positionId: $positionId
            tag: $tag
        ) {
            highlighted
        }
    }
`;
